import { useQuery } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { useState } from 'react';
import { Button, Col, Container, Row } from 'react-bootstrap';
import { Route, Routes } from 'react-router-dom';

import { getProducts } from '../../apis/queries';
import EmptyTableState from '../../components/EmptyState/EmptyStateTable';
import Loader from '../../components/Loader';
import { currencyFormatter } from '../../helpers/currencyFormatter';
import { Product } from '../../types';
import AnalyticsHeader from '../../widgets/Header';
import CreateProductModal from './CreateProductModal';
import TableList from './TableList';
import ProductDetail from './ViewAndEditDetail/ViewAndEditDetail';

export const PRODUCTS_QUERY_KEY = ['products'];

const CreateProductsButton = ({ onClick }: { onClick: () => void }) => {
  return <Button onClick={onClick}> Create Product </Button>;
};

const columnHelper = createColumnHelper<Product>();

const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('price', {
    header: 'Price',
    cell: info => currencyFormatter(info.getValue()),
    footer: info => info.column.id
  }),
  columnHelper.accessor('code', {
    header: 'Code',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('qty', {
    header: 'Qty',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('stockAtHand', {
    header: 'Stock At Hand',
    cell: info => info.getValue(),
    footer: info => info.column.id
  }),
  columnHelper.accessor('requiresEmpties', {
    header: 'Requires Empties ',
    cell: info => (info.getValue() ? 'True' : 'False'),
    footer: info => info.column.id
  })
];

interface ProductTableProps {
  query: Record<string, unknown>;
}

const ProductsTable = ({ query }: ProductTableProps) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const compositeQueryKey = [...PRODUCTS_QUERY_KEY, query];
  const { data, isLoading } = useQuery({ queryKey: compositeQueryKey, queryFn: () => getProducts(query) });
  const products = data?.results;

  const onCreateButtonClicked = () => {
    setShowCreateModal(true);
  };

  const onCreateModalClosed = () => {
    setShowCreateModal(false);
  };

  return (
    <>
      {showCreateModal && <CreateProductModal show={showCreateModal} handleClose={onCreateModalClosed} />}
      <AnalyticsHeader
        title="Products"
        headerButton={<CreateProductsButton onClick={onCreateButtonClicked} />}
        tabs={tabRoutes}
      />
      <Container fluid>
        <Row className="justify-content-center">
          <Col xs={12}>
            {isLoading ? (
              <Loader />
            ) : products && products.length > 0 ? (
              <TableList products={products} columns={columns} />
            ) : (
              <EmptyTableState message="No products yet." />
            )}
          </Col>
        </Row>
      </Container>
    </>
  );
};

const tabRoutes = [
  {
    title: 'Active',
    path: 'active',
    tabLink: '/products/active',
    element: <ProductsTable query={{ isActive: true }} />
  },
  {
    title: 'Inactive',
    path: 'inactive',
    tabLink: '/products/inactive',
    element: <ProductsTable query={{ isActive: false }} />
  }
];

const productRoutes = [
  {
    path: '/:status/:id',
    element: <ProductDetail />
  }
];

const Products = () => {
  return (
    <>
      <div className="main-content">
        <Routes>
          {tabRoutes.map(({ path, element, title }) => (
            <Route key={title} path={path} element={element} />
          ))}
          {productRoutes.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
        </Routes>
      </div>
    </>
  );
};

export default Products;
